import React, { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { Card } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import {
  HelpCircle, CheckCircle2, LayoutDashboard, FolderKanban, LayoutGrid, Contact,
  BarChart3, Target, UserCog, Calendar, MapPin, Mail, Keyboard, MessageCircleQuestion,
  Rocket, ChevronRight, RotateCcw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const RESTRICTED_MODULES_B2B = ['KPI Dashboard', 'Kadry / Usługi', 'Ustawienia', 'Użytkownicy'];

const STEPS = [
  { num: 1, text: 'Zaloguj się do systemu swoimi danymi.' },
  { num: 2, text: 'Otwórz Dashboard — zobaczysz najważniejsze wskaźniki.' },
  { num: 3, text: 'Stwórz pierwszy projekt — kliknij „Nowy projekt" na liście zleceń.' },
  { num: 4, text: 'Zmień status projektu na tablicy Kanban — przeciągnij kartę.' },
  { num: 5, text: 'Wyślij email do klienta z karty projektu.' },
];

const MODULES = [
  { name: 'Dashboard', icon: LayoutDashboard, desc: 'Strona główna z podsumowaniem: liczba projektów, wartość pipeline, najnowsze zlecenia. Tu widzisz swoje najważniejsze wskaźniki w jednym miejscu.' },
  { name: 'Zlecenia (Projekty)', icon: FolderKanban, desc: 'Lista wszystkich projektów z możliwością filtrowania i wyszukiwania. Dodawaj nowe projekty, edytuj istniejące i zarządzaj kosztami.' },
  { name: 'Kanban (Tablica)', icon: LayoutGrid, desc: 'Wizualna tablica z kolumnami statusów. Przeciągaj karty między kolumnami aby zmieniać statusy projektów w intuicyjny sposób.' },
  { name: 'Klienci', icon: Contact, desc: 'Baza klientów z tagami i segmentacją. Przeglądaj historię kontaktów, powiązane projekty i całkowitą wartość współpracy.' },
  { name: 'Lejek sprzedażowy', icon: Target, desc: 'Wizualizacja procesu sprzedaży od leada do zakończenia. Analizuj konwersje między etapami i średnie czasy realizacji.' },
  { name: 'KPI Dashboard', icon: BarChart3, desc: 'Zaawansowane wskaźniki efektywności: realizacja celów, porównania okresowe, wykresy trendów. Dostępny dla administratorów.' },
  { name: 'Kadry / Usługi', icon: UserCog, desc: 'Zarządzanie godzinami pracy, usługami i kosztami robocizny. Rejestruj czas pracy zespołu montażowego.' },
  { name: 'Planer', icon: Calendar, desc: 'Kalendarz zadań montażowych. Planuj i przydzielaj zadania zespołowi, śledź terminy i postępy realizacji.' },
  { name: 'Raporty', icon: BarChart3, desc: 'Zestawienia finansowe, ranking handlowców, lejek sprzedażowy. Filtruj dane po okresie i użytkowniku.' },
  { name: 'Email', icon: Mail, desc: 'Wysyłanie maili do klientów bezpośrednio z karty projektu. Używaj szablonów i śledź historię korespondencji.' },
];

const SHORTCUTS = [
  { keys: 'Ctrl + K', desc: 'Otwórz wyszukiwarkę globalną — szukaj projektów, klientów i adresów.' },
  { keys: 'Tab', desc: 'Nawigacja między polami w formularzach.' },
  { keys: 'Enter', desc: 'Zatwierdź formularz lub wybór.' },
  { keys: 'Esc', desc: 'Zamknij okno dialogowe lub modal.' },
];

const FAQ = [
  { q: 'Jak dodać nowy projekt?', a: 'Przejdź do zakładki „Zlecenia" i kliknij przycisk „Nowy projekt" w prawym górnym rogu. Wypełnij formularz z danymi klienta i kosztami.' },
  { q: 'Jak zmienić status projektu?', a: 'Na tablicy Kanban przeciągnij kartę projektu do odpowiedniej kolumny. Możesz też zmienić status na stronie szczegółów projektu z listy rozwijanej.' },
  { q: 'Jak wysłać email do klienta?', a: 'Otwórz kartę projektu i kliknij przycisk „Wyślij email". Wybierz szablon wiadomości lub napisz własną treść.' },
  { q: 'Jak wpisać godziny pracy?', a: 'Przejdź do modułu „Kadry / Usługi". Kliknij „Dodaj wpis" i wypełnij datę, liczbę godzin oraz opis wykonanych prac.' },
  { q: 'Jak zainstalować aplikację na telefonie?', a: 'Otwórz CRM w przeglądarce na telefonie. Kliknij „Dodaj do ekranu głównego" (Android) lub „Udostępnij → Dodaj do Home Screen" (iPhone). Aplikacja zainstaluje się jako PWA.' },
];

export const Help = () => {
  const { user, setUser } = useAuth();
  const isB2B = user?.role === 'handlowiec_b2b';

  const filteredModules = isB2B
    ? MODULES.filter(m => !RESTRICTED_MODULES_B2B.includes(m.name))
    : MODULES;

  const restartOnboarding = async () => {
    try {
      if (setUser) {
        setUser(prev => {
          const updated = { ...prev, onboarding_completed: false };
          localStorage.setItem('user', JSON.stringify(updated));
          return updated;
        });
      }
      toast.success('Onboarding zostanie pokazany po odświeżeniu strony.');
      setTimeout(() => window.location.reload(), 800);
    } catch {
      toast.error('Błąd');
    }
  };

  return (
    <Layout>
      <div className="p-4 md:p-6 lg:p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-manrope font-bold text-[var(--crm-text-primary)] tracking-tight flex items-center gap-3" data-testid="help-title">
              <HelpCircle className="w-8 h-8 text-[#FF6B35]" />
              Pomoc
            </h1>
            <p className="text-[var(--crm-text-secondary)] mt-1">Przewodnik po systemie CRM Trendhomes</p>
          </div>
          <Button onClick={restartOnboarding} data-testid="restart-onboarding-btn"
            className="bg-[var(--crm-bg-dialog)] hover:bg-[#2d4a73] text-[var(--crm-text-secondary)] border border-[var(--crm-border)]">
            <RotateCcw className="w-4 h-4 mr-2" />
            Pokaż ponownie
          </Button>
        </div>

        {/* Jak zacząć */}
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 mb-6" data-testid="help-getting-started">
          <h2 className="text-lg font-bold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">
            <Rocket className="w-5 h-5 text-[#FF6B35]" />
            Jak zacząć
          </h2>
          <div className="space-y-3">
            {STEPS.map(s => (
              <div key={s.num} className="flex items-start gap-3 group">
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-[#FF6B35]/15 text-[#FF6B35] flex items-center justify-center text-sm font-bold">
                  {s.num}
                </div>
                <p className="text-[var(--crm-text-secondary)] text-sm leading-relaxed pt-0.5">{s.text}</p>
              </div>
            ))}
          </div>
        </Card>

        {/* Moduły CRM */}
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 mb-6" data-testid="help-modules">
          <h2 className="text-lg font-bold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">
            <LayoutDashboard className="w-5 h-5 text-[#FF6B35]" />
            Moduły CRM
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {filteredModules.map((m) => {
              const Icon = m.icon;
              return (
                <AccordionItem key={m.name} value={m.name} className="border-[var(--crm-border)]">
                  <AccordionTrigger className="text-slate-200 hover:text-[var(--crm-text-primary)] hover:no-underline py-3">
                    <span className="flex items-center gap-2.5">
                      <Icon className="w-4 h-4 text-[#FF6B35]" />
                      {m.name}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="text-[var(--crm-text-secondary)] text-sm leading-relaxed pl-7">
                    {m.desc}
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </Card>

        {/* Skróty klawiszowe */}
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6 mb-6" data-testid="help-shortcuts">
          <h2 className="text-lg font-bold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">
            <Keyboard className="w-5 h-5 text-[#FF6B35]" />
            Skróty klawiszowe
          </h2>
          <div className="space-y-2">
            {SHORTCUTS.map((s) => (
              <div key={s.keys} className="flex items-center gap-4 py-2">
                <kbd className="bg-[var(--crm-bg-panel)] border border-[var(--crm-border)] text-slate-200 px-3 py-1 rounded-md text-xs font-mono min-w-[90px] text-center">
                  {s.keys}
                </kbd>
                <span className="text-[var(--crm-text-secondary)] text-sm">{s.desc}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* FAQ */}
        <Card className="bg-[var(--crm-bg-dialog)] border-[var(--crm-border)] p-6" data-testid="help-faq">
          <h2 className="text-lg font-bold text-[var(--crm-text-primary)] mb-4 flex items-center gap-2">
            <MessageCircleQuestion className="w-5 h-5 text-[#FF6B35]" />
            Najczęstsze pytania (FAQ)
          </h2>
          <Accordion type="single" collapsible className="w-full">
            {FAQ.map((f, i) => (
              <AccordionItem key={f.q} value={f.q} className="border-[var(--crm-border)]">
                <AccordionTrigger className="text-slate-200 hover:text-[var(--crm-text-primary)] hover:no-underline py-3 text-left">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-[var(--crm-text-secondary)] text-sm leading-relaxed">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </Card>
      </div>
    </Layout>
  );
};
